import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FilterOptionsModel, FilterValue } from '../../organisms/data-table/data-table.types';

export type { FilterOptionsModel, FilterValue };

/**
 * Molécula: botón "Filtros" con panel y flyout lateral por grupo.
 * Usa position:fixed para escapar de cualquier overflow:hidden ancestro.
 */
@Component({
  selector: 'ui-filter-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './filter-menu.component.html',
  styleUrl: './filter-menu.component.scss',
})
export class UiFilterMenuComponent {
  readonly filters = input<FilterOptionsModel[]>([]);
  readonly change  = output<FilterOptionsModel[]>();

  protected readonly isOpen          = signal(false);
  protected readonly panelRect       = signal<{ top: number; left: number } | null>(null);
  protected readonly hoveredGroupIdx = signal<number | null>(null);
  protected readonly flyoutRect      = signal<{ top: number; left: number } | null>(null);

  protected readonly activeCount = computed(() =>
    this.filters().flatMap(g => g.values).filter(v => v.selected).length,
  );

  private flyoutCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly elRef: ElementRef<HTMLElement>) {
    const onScroll = () => { if (this.isOpen()) this.close(); };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    inject(DestroyRef).onDestroy(() =>
      document.removeEventListener('scroll', onScroll, { capture: true }),
    );
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('keydown.escape')
  onEsc(): void { this.close(); }

  @HostListener('window:resize')
  onResize(): void { if (this.isOpen()) this.close(); }

  protected toggle(event: MouseEvent): void {
    event.stopPropagation();
    this.isOpen() ? this.close() : this.open(event);
  }

  private open(event: MouseEvent): void {
    const btn  = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.panelRect.set({ top: rect.bottom + 4, left: rect.left });
    this.isOpen.set(true);
  }

  protected close(): void {
    this.clearFlyoutTimer();
    this.isOpen.set(false);
    this.panelRect.set(null);
    this.hoveredGroupIdx.set(null);
    this.flyoutRect.set(null);
  }

  // ── Flyout por grupo ──────────────────────────────────────────────────────

  private readonly FLYOUT_WIDTH = 176; // 11rem ≈ 176px

  protected onGroupEnter(event: MouseEvent, idx: number): void {
    this.clearFlyoutTimer();
    const row  = event.currentTarget as HTMLElement;
    const rect = row.getBoundingClientRect();
    this.hoveredGroupIdx.set(idx);
    // Flyout a la derecha del panel; si no cabe, a la izquierda
    const rightPos = rect.right + 4;
    const fitsRight =
      rightPos + this.FLYOUT_WIDTH <= window.innerWidth;
    this.flyoutRect.set({
      top: rect.top,
      left: fitsRight ? rightPos : rect.left - this.FLYOUT_WIDTH - 4,
    });
  }

  protected onGroupLeave(): void {
    this.flyoutCloseTimer = setTimeout(() => this.clearFlyout(), 100);
  }

  protected onFlyoutEnter(): void {
    this.clearFlyoutTimer();
  }

  protected onFlyoutLeave(): void {
    this.flyoutCloseTimer = setTimeout(() => this.clearFlyout(), 100);
  }

  private clearFlyout(): void {
    this.hoveredGroupIdx.set(null);
    this.flyoutRect.set(null);
  }

  private clearFlyoutTimer(): void {
    if (this.flyoutCloseTimer !== null) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  // ── Lógica de selección ───────────────────────────────────────────────────

  protected toggleValue(group: FilterOptionsModel, val: FilterValue): void {
    const updated = this.filters().map(g =>
      g === group
        ? { ...g, values: g.values.map(v => v === val ? { ...v, selected: !v.selected } : v) }
        : g,
    );
    this.change.emit(updated);
  }

  protected clearAll(event: MouseEvent): void {
    event.stopPropagation();
    const updated = this.filters().map(g => ({
      ...g,
      values: g.values.map(v => ({ ...v, selected: false })),
    }));
    this.change.emit(updated);
  }

  protected groupActiveCount(group: FilterOptionsModel): number {
    return group.values.filter(v => v.selected).length;
  }
}
